import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { PhotoBadges, type PhotoFlag } from "./PhotoBadges";
import { laneCount, laneOf, TILE_GAP } from "./lanes";

export type PhotoTile = {
  id: string;
  /**
   * Địa chỉ ảnh. Để rỗng khi màn tự vẽ ảnh qua `renderImage` — vài màn lấy ảnh
   * bằng component ký URL theo từng tấm chứ không có sẵn một chuỗi.
   */
  url: string;
  /**
   * null = CHƯA ĐO (xem migration 0396). KHÔNG phải ảnh vuông — lưới để ảnh tự
   * quyết chiều cao thay vì bịa ra một tỉ lệ.
   */
  width: number | null;
  height: number | null;
  flags: PhotoFlag[];
  /** Lớp phủ riêng của từng màn: ô tích của khách, nút nộp bản sửa… */
  overlay?: ReactNode;
  /** Lời cho người đọc màn hình. Không hiện thành chữ trên ô. */
  label?: string;
  /**
   * Bề ngang mong muốn của một ô, pixel. Mặc định là hằng dùng chung.
   *
   * CHỈ truyền ở những chỗ ảnh không phải nội dung chính — dải ảnh xác nhận
   * trong cột hẹp chẳng hạn: ở đó ô cỡ tường ảnh sẽ đẩy khung ký ra khỏi màn.
   * Tường ảnh thì đừng truyền, để đổi một hằng là mọi tường to theo.
   */
  targetWidth?: number;
  /**
   * Chữ hiện khi RÊ CHUỘT, đè lên ảnh.
   *
   * Vắng thì lưới tự đánh số theo vị trí. Màn nào có số tấm RIÊNG phải truyền
   * vào đây: đơn chờ sửa đánh số theo `seq` khách thấy trên iPad, mà danh sách
   * ở đó có lọc — để lưới tự đếm là thợ tra "ảnh 03" ra nhầm tấm, im lặng.
   */
  caption?: string;
  /**
   * Ô này có bấm được không. Mặc định CÓ.
   *
   * Đặt false khi màn không có gì để mở cho tấm đó — ví dụ ảnh nằm ngoài hàng
   * đợi sửa: lễ tân mở ra cũng không nộp được bản sửa. Một cái nút bấm vào
   * không xảy ra gì là lời hứa suông, và người dùng bàn phím phải Tab qua nó.
   */
  interactive?: boolean;
  /**
   * Ô này là một CÔNG TẮC đang bật/tắt (khách tích chọn ảnh), không phải một
   * cái nút mở ra thứ khác. Có mặt thì nút mang `aria-pressed` — người đọc màn
   * hình nghe được tấm nào đang chọn mà không cần nhìn dấu tích.
   */
  pressed?: boolean;
  /**
   * Bấm không được lúc này (album đã chốt, đang gửi lệnh lên máy chủ).
   *
   * Khác `interactive: false`: ở đây VẪN là một cái nút, chỉ là đang khoá —
   * người dùng biết chỗ này bình thường bấm được. `interactive: false` là
   * "chỗ này không bao giờ bấm được".
   */
  disabled?: boolean;
  /**
   * Tấm đang MỞ ở panel chi tiết bên cạnh. Viền nâu 3px vẽ VÀO TRONG mép ảnh
   * (bản xem thử `.t.sel`), không đẩy lưới lệch đi một điểm ảnh nào.
   *
   * Đây là dấu "đang cầm tấm này", KHÔNG phải tình trạng: tình trạng vẫn chỉ
   * báo bằng huy hiệu tròn (luật CEO 26/09 — không vệt màu nào chạm mép ảnh
   * để nói tình trạng).
   */
  selected?: boolean;
  /** Tấm đang ở lần sửa thứ mấy — huy hiệu `v2` in đúng số đó ("V3"). */
  version?: number;
};

/**
 * Lưới ảnh DÙNG CHUNG cho cả 11 màn bày ảnh khách.
 *
 * Luật CEO đã chốt 26/09/2026 và ĐỪNG phá:
 *  - Ôm ĐÚNG khổ từng tấm, không cắt tấm nào.
 *  - KHÔNG chữ nào trên ô. Số tấm chỉ hiện khi rê chuột.
 *  - KHÔNG một vệt màu nào chạm mép ảnh — tình trạng báo bằng huy hiệu tròn.
 *  - Khe 2px, không bo góc, không viền.
 *
 * ⚠️ Màn đang ẩn có `clientWidth` = 0. Hàm đo leo lên tổ tiên tìm bề ngang đo
 * được, vì nếu rơi về số làn cứng thì đổi hằng cỡ ô KHÔNG có tác dụng ở mọi
 * màn chưa mở — và im lặng: màn đầu to đúng nên tưởng đã xong.
 */
export function PhotoMasonry({
  tiles,
  onOpen,
  emptyText = "Chưa có ảnh nào.",
  renderImage,
  label,
  targetWidth,
}: {
  tiles: PhotoTile[];
  onOpen: (tile: PhotoTile) => void;
  emptyText?: string;
  /**
   * Tên của cả lưới, cho người đọc màn hình và cho bài kiểm gọi đúng lưới.
   *
   * Cần vì vài trang có HAI lưới ảnh cạnh nhau (ảnh buổi chụp và ảnh in), và
   * nhãn trên ô của lưới này trùng chữ với ô đếm của dải tình trạng — không có
   * tên thì không ai phân biệt được lưới nào.
   */
  label?: string;
  /**
   * Bề ngang mong muốn của một ô, pixel. Mặc định là hằng dùng chung.
   *
   * CHỈ truyền ở những chỗ ảnh không phải nội dung chính — dải ảnh xác nhận
   * trong cột hẹp chẳng hạn: ở đó ô cỡ tường ảnh sẽ đẩy khung ký ra khỏi màn.
   * Tường ảnh thì đừng truyền, để đổi một hằng là mọi tường to theo.
   */
  targetWidth?: number;
  /**
   * Cách vẽ ảnh của màn này. Vắng thì lưới dùng thẻ `<img src={tile.url}>`.
   *
   * Có mặt vì vài màn không cầm sẵn địa chỉ ảnh: chúng gọi một component ký
   * URL ngắn hạn theo từng tấm (StaffPhoto, SignedAssetTile…). Bắt chúng lấy
   * URL trước rồi truyền vào là phải gọi hook trong vòng lặp — React cấm.
   */
  renderImage?: (tile: PhotoTile) => ReactNode;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const update = () => setWidth(measurableWidth(el));
    update();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tiles.length]);

  const lanes = laneCount(width, targetWidth);
  const buckets = useMemo(() => {
    const out: PhotoTile[][] = Array.from({ length: lanes }, () => []);
    tiles.forEach((t, i) => out[laneOf(i, lanes)]!.push(t));
    return out;
  }, [tiles, lanes]);

  if (tiles.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{emptyText}</p>;
  }

  return (
    // role="list" + "listitem": lưới ảnh LÀ một danh sách. Làn chỉ là chỗ xếp
    // nên mang role="presentation" — không thì người đọc màn hình nghe ra hai
    // ba danh sách lồng nhau thay vì một danh sách ảnh.
    <div
      ref={boxRef}
      role="list"
      {...(label ? { "aria-label": label } : {})}
      className="flex"
      style={{ gap: TILE_GAP }}
      data-testid="photo-masonry"
    >
      {buckets.map((lane, laneIndex) => (
        <div
          key={laneIndex}
          role="presentation"
          className="flex min-w-0 flex-1 flex-col"
          style={{ gap: TILE_GAP }}
        >
          {lane.map((t, pos) => (
            // Thẻ BỌC, không phải nút.
            //
            // Lớp phủ của từng màn mang nút thật (gỡ ảnh, tích chọn), mà nút
            // lồng trong nút là HTML sai: trình duyệt tự gỡ lồng, bàn phím
            // Tab không tới được nút trong, và người đọc màn hình nghe ra một
            // nút duy nhất. Nút mở ảnh vì thế là em ruột của lớp phủ, không
            // phải cha nó.
            <div key={t.id} role="listitem" className="group relative">
              {(() => {
                const ten = t.label ?? t.caption ?? soTam(pos, lanes, laneIndex);
                // Ô bấm được thì NÚT mang tên, ảnh để alt rỗng — không thì
                // người đọc màn hình nghe tên hai lần. Ô KHÔNG bấm được thì
                // không có nút nào mang tên cả, nên tên phải nằm ở alt: để
                // rỗng ở đó là tấm ảnh biến mất hẳn khỏi trình đọc màn hình.
                const alt = t.interactive === false ? ten : "";
                const anh = renderImage ? (
                  renderImage(t)
                ) : (
                  <img
                    src={t.url}
                    {...(t.width && t.height ? { width: t.width, height: t.height } : {})}
                    alt={alt}
                    loading="lazy"
                    decoding="async"
                    className={t.width && t.height ? "h-full w-full object-cover" : "block w-full"}
                  />
                );
                const khung =
                  // Chưa đo thì KHÔNG đặt aspect-ratio — để ảnh tự quyết chiều
                  // cao. Gán 1/1 là bịa; ảnh dọc sẽ hiện vuông mà không ai biết.
                  t.width && t.height ? { aspectRatio: `${t.width} / ${t.height}` } : {};
                const chon = t.selected
                  ? // Không z-index: viền vẽ vào trong nên không cần nổi lên ô
                    // bên cạnh, còn nâng ô lên là ảnh đè mất huy hiệu của chính nó.
                    { outline: "3px solid var(--color-brand)", outlineOffset: "-3px" }
                  : {};
                const chung = {
                  "data-testid": `tile-${t.id}`,
                  ...(t.selected ? { "data-selected": "true" } : {}),
                  // Không border / ring / padding / shadow: mép ảnh phải sạch màu.
                  className: "block w-full overflow-hidden bg-muted",
                  style:
                    t.selected || (t.width && t.height) ? { ...khung, ...chon } : undefined,
                };
                // Ô không bấm được thì KHÔNG dựng ra cái nút: một cái nút bấm
                // vào không xảy ra gì là lời hứa suông, và người dùng bàn phím
                // vẫn phải Tab qua nó.
                return t.interactive === false ? (
                  <div {...chung}>{anh}</div>
                ) : (
                  <button
                    {...chung}
                    type="button"
                    onClick={() => onOpen(t)}
                    {...(t.disabled ? { disabled: true } : {})}
                    {...(t.pressed === undefined ? {} : { "aria-pressed": t.pressed })}
                    aria-label={t.label ?? `Mở ${t.caption ?? soTam(pos, lanes, laneIndex)}`}
                  >
                    {anh}
                  </button>
                );
              })()}
              <PhotoBadges flags={t.flags} {...(t.version !== undefined ? { version: t.version } : {})} />
              {t.overlay}
              {/* Số tấm chỉ hiện khi RÊ CHUỘT, đè lên ảnh — không chiếm một
                  milimet nào của lưới. aria-hidden vì nhãn đã nằm ở aria-label
                  của nút; đọc hai lần là thừa.

                  ⚠️ KHÔNG dựng lại công tắc "hiện sẵn". Bản đầu có một cái và 8
                  màn bật nó lên, nên mọi ô ảnh lại mang dải chữ dưới đáy — đúng
                  thứ bản xem thử đã gỡ. Lý lẽ nghe xuôi ("thợ tra ảnh 03",
                  "iPad không có chuột") nhưng nó thắng một quyết định đã chốt,
                  mà lại thắng im lặng vì không cổng nào canh. Màn cần TÌM theo
                  số thì làm ô tìm kiếm, đừng in số lên mọi tấm. */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/75 to-transparent px-2 pb-1 pt-3 text-left text-[11.5px] text-white opacity-0 transition-opacity group-hover:opacity-100"
              >
                {t.caption ?? soTam(pos, lanes, laneIndex)}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Số hiện lên khi rê chuột — là thứ tự tấm trong DANH SÁCH GỐC, không phải thứ
 * tự trong làn. Tấm ở làn `laneIndex`, vị trí `pos` vốn là tấm thứ
 * `pos * lanes + laneIndex` của danh sách (xem laneOf).
 */
function soTam(pos: number, lanes: number, laneIndex: number): string {
  return `Tấm ${String(pos * lanes + laneIndex + 1).padStart(2, "0")}`;
}

/**
 * Bề ngang đo được của khối — leo lên tổ tiên khi chính nó đang ẩn.
 *
 * Màn chưa mở (thẻ khác đang hiển thị) cho `clientWidth` = 0, và nếu chấp nhận
 * số 0 thì lưới rơi về số làn tối thiểu ở MỌI màn chưa mở, im lặng. Màn đầu
 * đúng nên rất dễ tưởng đã xong — phải đo đủ cả ba màn mới thấy.
 */
function measurableWidth(el: HTMLElement): number {
  let node: HTMLElement | null = el;
  while (node) {
    if (node.clientWidth > 0) return node.clientWidth;
    node = node.parentElement;
  }
  return 0;
}
